import {useCallback, useState} from 'react';
import type {SubCategoryType} from '~/common/components/MegaNav/MegaNavVariant/types';
import {Button} from '~library/atoms/Button';
import {Link} from '~library/atoms/Link';
import {Typography} from '~library/atoms/Typography';
import {useTracking} from '~library/context/tracking/tracking-context';
import * as styles from './SubcategorySection.module.scss';

type SubcategorySectionProps = {
  subCategory: SubCategoryType;
  closeNav: () => void;
};

const MAX_SUB_CATEGORY = 9;

export const SubcategorySection = ({subCategory, closeNav}: SubcategorySectionProps) => {
  const [showingAll, setShowingAll] = useState(false);
  const {track} = useTracking();

  const {categories} = subCategory;

  const categoriesToDisplay = showingAll ? categories : categories?.slice(0, MAX_SUB_CATEGORY);

  const onViewMoreClick = useCallback(() => {
    track({
      dataQaId: 'view-more',
      subcategory: subCategory.display_name,
    });
    setShowingAll(true);
  }, [subCategory.display_name, track]);

  const handleLinkClick = useCallback(
    // biome-ignore lint/style/useNamingConvention: property comes from the API
    (item: {display_name: string; href: string; dataQaId: string}) => {
      track({
        dataQaId: item.dataQaId,
        href: item.href,
        title: item.display_name,
        subcategory: subCategory.display_name,
      });

      // Close drawer if the link contains a hash AND the base URL is the same
      // (meaning browser won't reload the page, just scroll to anchor)
      if (item.href.includes('#')) {
        const linkUrl = item.href.split('#')[0];
        const currentUrl = `${window.location.pathname}${window.location.search}`;

        if (linkUrl === currentUrl) {
          closeNav();
        }
      }
    },
    [subCategory.display_name, track, closeNav]
  );

  return (
    <div className={styles.container}>
      <Typography
        variant="paragraph4"
        className="meganav-subcategory-header"
        data-qa-id={subCategory.dataQaId}
        color="coin"
        semibold
      >
        {subCategory.display_name}
      </Typography>

      <div className={styles.content}>
        {categoriesToDisplay?.map((item) => (
          <Link
            key={item.display_name}
            href={item.href}
            data-qa-id={item.dataQaId}
            onClick={() => handleLinkClick(item)}
          >
            <Typography variant="paragraph2" color="charcoal" className={styles.text}>
              {item.display_name}
            </Typography>
          </Link>
        ))}
        {!showingAll && (categoriesToDisplay?.length ?? 0) < (categories?.length ?? 0) && (
          <Button
            onClick={onViewMoreClick}
            data-qa-id="view-more"
            variant="link"
            className={styles.viewMore}
          >
            View more
          </Button>
        )}
      </div>
    </div>
  );
};
